import { useState } from 'react';

import {
  Button,
  Center,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Image,
  PinInput,
  PinInputField,
  Spinner,
  Text,
  useBoolean,
  VStack,
} from '@chakra-ui/react';

import { Controller, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import {
  CheckPhoneNumberUpdateResponse,
  getError,
  Individual,
  useToast,
} from '@aslanpay/portal-common-lib';

import backgroundLogo from 'app/assets/svg/backgroundLogo.svg';
import BrandLogo from 'app/components/Branding/BrandLogo';

import ReferralCodeField from 'app/components/Authentication/ReferralCodeField';
import SignUpLegalLinks from 'app/components/Authentication/SignUpLegalLinks';
import PhoneNumberInput from 'app/components/UI/Inputs/PhoneNumberInput';

import useActivateIndividual from 'app/hooks/useActivateIndividual';
import useChangePhoneNumber from 'app/hooks/useChangePhoneNumber';
import useCheckPhoneNumberUpdate from 'app/hooks/useCheckPhoneNumberUpdate';
import useFinishSignUp from 'app/hooks/useFinishSignUp';
import useReferralCode from 'app/hooks/useReferralCode';

import { authenticationSchema } from 'app/utils/schemas';

import { EnableTwoFactorFormFields } from '../types';

interface SsoActivationProps {
  individual: Individual;
}

type ActivationStep = 'phone' | 'verify';

/**
 * Blocking activation step shown after the SSO callback for an employee who federated in but never
 * activated their record. It cannot be skipped or dismissed: the employee provides a mobile number
 * (unless one is already verified), an SMS code confirms it, the record is activated, and the
 * employee lands in the employee portal. An already-active employee never sees this.
 *
 * Order matches mobile — send code, then activate, then confirm the number: activation creates the
 * Tribe holder that `changePhoneNumber` needs to sync. If the confirm step is abandoned the record
 * is already ACTIVE with a verified number, so a page reload simply lands the employee in.
 */
function SsoActivation({ individual }: SsoActivationProps) {
  const toast = useToast();
  const finishActivation = useFinishSignUp();
  const referral = useReferralCode();

  const phoneAlreadyVerified = !!individual.phoneNumber && individual.phoneNumberVerified;

  const [step, setStep] = useState<ActivationStep>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [verifyError, setVerifyError] = useState('');
  const [resendMessage, setResendMessage] = useState('Code not received?');
  const [isResendDisabled, setIsResendDisabled] = useBoolean();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<EnableTwoFactorFormFields>({
    mode: 'onChange',
    resolver: yupResolver(authenticationSchema.enableTwoFactor),
    defaultValues: { phoneNumber: '' },
  });

  const showErrorToast = (error: Error) => {
    const isRateLimited = getError(error).status === 429;
    toast({
      position: 'top-right',
      status: 'error',
      title: isRateLimited ? 'Too many attempts' : 'Something went wrong',
      description: isRateLimited ? 'Please try again later.' : 'Please try again.',
    });
  };

  const { mutate: activate, isLoading: isActivating } = useActivateIndividual({
    onSuccess: () => {
      if (phoneAlreadyVerified) {
        finishActivation('EMPLOYEE');
        return;
      }
      setStep('verify');
    },
    onError: showErrorToast,
  });

  const { mutate: sendCode, isLoading: isSendingCode } = useCheckPhoneNumberUpdate({
    onSuccess: (data: CheckPhoneNumberUpdateResponse) => {
      setTransactionId(data.transactionId);
      activate({
        id: individual.id,
        phoneNumber,
        referralCode: referral.referralCode || undefined,
      });
    },
    onError: showErrorToast,
  });

  const { mutate: confirmPhone, isLoading: isConfirmingPhone } = useChangePhoneNumber({
    onSuccess: () => finishActivation('EMPLOYEE'),
    onError: (error) =>
      setVerifyError(
        getError(error).status === 403
          ? 'This code is incorrect. Please try again.'
          : 'Something went wrong. Please try again.'
      ),
  });

  const { mutate: resendCode, isLoading: isResendingCode } = useCheckPhoneNumberUpdate({
    onSuccess: (data: CheckPhoneNumberUpdateResponse) => setTransactionId(data.transactionId),
    onError: showErrorToast,
  });

  const handleResendCode = () => {
    setVerifyError('');
    resendCode(phoneNumber);
    setIsResendDisabled.on();
    setResendMessage('A new verification code was sent!');
    setTimeout(() => {
      setResendMessage('Code not received?');
      setIsResendDisabled.off();
    }, 120000);
  };

  const handlePhoneSubmit = async (data: EnableTwoFactorFormFields) => {
    if ((await referral.validate()) === false) return;

    if (phoneAlreadyVerified) {
      activate({
        id: individual.id,
        phoneNumber: individual.phoneNumber,
        referralCode: referral.referralCode || undefined,
      });
      return;
    }

    const normalised = data.phoneNumber.split(' ').join('');
    setPhoneNumber(normalised);
    sendCode(normalised);
  };

  const isBusy = referral.isValidating || isSendingCode || isActivating;

  const renderPhoneStep = () => (
    <form onSubmit={handleSubmit(handlePhoneSubmit)} noValidate>
      <Text as="h2" textStyle="h2" color="brand.darkPurple" mt="25px">
        Activate your account
      </Text>
      <Text as="h5" textStyle="h5Medium" color="brand.mutedPurple" mt="15px" mb="35px">
        {phoneAlreadyVerified
          ? 'Confirm the details below to finish setting up your employee account.'
          : 'Enter your mobile number to finish setting up your employee account.'}
      </Text>

      {!phoneAlreadyVerified && (
        <FormControl isInvalid={!!errors?.phoneNumber?.message} isRequired>
          <FormLabel>Mobile number</FormLabel>
          <Controller
            control={control}
            name="phoneNumber"
            render={({ field: { onChange, onBlur, value } }) => (
              <PhoneNumberInput
                defaultValue={value}
                placeholder="Mobile number"
                onChange={onChange}
                onBlur={onBlur}
              />
            )}
          />
          <FormErrorMessage>{errors?.phoneNumber?.message}</FormErrorMessage>
        </FormControl>
      )}

      <Flex mt="20px">
        <ReferralCodeField
          value={referral.referralCode}
          error={referral.error}
          isValidating={referral.isValidating}
          onChange={referral.handleChange}
        />
      </Flex>

      <SignUpLegalLinks />

      <Button
        data-testid="sso-activation-submit"
        w="full"
        size="lg"
        type="submit"
        isLoading={isBusy}
        isDisabled={(!phoneAlreadyVerified && !isValid) || !!referral.error}
      >
        {phoneAlreadyVerified ? 'Complete setup' : 'Send code'}
      </Button>
    </form>
  );

  const renderVerifyStep = () => (
    <Flex flexDir="column" mt="25px">
      <Text as="h2" textStyle="h2" color="brand.darkPurple">
        Confirm your mobile number
      </Text>
      <Text as="h5" textStyle="h5Medium" color="brand.mutedPurple" mt="15px" mb="35px">
        Enter the verification code we sent to {phoneNumber}.
      </Text>

      {isConfirmingPhone ? (
        <Center w="full" py="20px">
          <Spinner color="brand.purple" emptyColor="brand.purpleAlpha30" size="xl" />
        </Center>
      ) : (
        <VStack spacing="10px">
          <HStack spacing="20px" justify="center">
            <PinInput
              placeholder=""
              onComplete={(code) => {
                setVerifyError('');
                confirmPhone({ phoneNumber, transactionId, code });
              }}
              size="lg"
              isInvalid={!!verifyError}
            >
              <PinInputField />
              <PinInputField />
              <PinInputField />
              <PinInputField />
              <PinInputField />
              <PinInputField />
            </PinInput>
          </HStack>
          {verifyError && (
            <Text textStyle="smallMedium" color="system.error">
              {verifyError}
            </Text>
          )}
        </VStack>
      )}

      <VStack mt="35px" spacing={0}>
        <Text textStyle="smallRegular" color="brand.darkGray">
          {resendMessage}
        </Text>
        <Button
          data-testid="sso-activation-resend"
          size="sm"
          variant="link"
          onClick={handleResendCode}
          isDisabled={isResendDisabled}
          isLoading={isResendingCode}
        >
          Resend code
        </Button>
      </VStack>
    </Flex>
  );

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="left"
      bgGradient="linear(to-r, brand.darkPurple 7.24%, brand.grayishBlack 115.47%)"
    >
      <Image src={backgroundLogo} position="absolute" right={0} />
      <Flex
        flexDirection="column"
        flexGrow={1}
        ml={{ base: '30px', md: '130px' }}
        mr={{ base: '30px', md: 'auto' }}
        maxW="500px"
        rounded="lg"
        bg="white"
        boxShadow="lg"
        py="40px"
        px="60px"
        zIndex={1}
      >
        <BrandLogo w="110px" />
        {step === 'phone' ? renderPhoneStep() : renderVerifyStep()}
      </Flex>
    </Flex>
  );
}

export default SsoActivation;
